import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Mail, ShieldCheck, Calendar, Clock, UserCog, LogOut } from 'lucide-react';
import MyContainer from '@/components/MyContainer';
import { useParams, useLocation } from 'wouter';
import { useUser } from '@/services/user-service';
import { formatDateLabel } from '@/lib/utils';
import { forceLogout } from '@/api/user-api';
import { toast } from '@/hooks/use-toast';

export default function ViewUserPage() {
    const params = useParams();
    const [, navigate] = useLocation();
    const userId = params.id || '';
    const {
        data: userResponse,
        isLoading: isLoadingUser,
        error: userError,
    } = useUser(userId);
    const user = userResponse;
    const handleForceLogout = async () => {
        const resp = await forceLogout(userId);
        if (resp.success) {
            toast({ title: "Success", description: "User force logged out successfully" });
        }
        else {
            toast({ title: "Error", description: "Failed to force logout user", variant: "destructive" });
        }
    }

    return (
        <MyContainer title="User Profile" subtitle="Manage internal team member permissions and activity">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border shadow-sm">
                    <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16 border">
                            <AvatarImage src={user?.photo || ''} />
                            <AvatarFallback className="bg-primary text-primary-foreground font-bold">
                                {user?.username.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <h1 className="text-2xl font-bold tracking-tight">{user?.username}</h1>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="font-medium">{user?.role}</Badge>
                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-none">
                                    {user?.state === 1 ? 'Active' : 'Inactive'}
                                </Badge>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 w-full md:w-auto">
                        <Button variant="outline" className="flex-1 md:flex-none" onClick={() => navigate(`/users/${user?.id}/edit`)}>
                            <UserCog className="mr-2 h-4 w-4" /> Edit User
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                    {/* Left Column: Account Details */}
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm font-semibold text-muted-foreground uppercase">Contact Information</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <Mail className="h-4 w-4 text-muted-foreground" />
                                    <div className="text-sm">
                                        <p className="text-muted-foreground text-xs">Email</p>
                                        <p className="font-medium">{user?.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <div className="text-sm">
                                        <p className="text-muted-foreground text-xs">Member Since</p>
                                        <p className="font-medium">{user?.createdAt && formatDateLabel(user?.createdAt) || 'N/A'}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm font-semibold text-muted-foreground uppercase">Security</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-2 text-muted-foreground">
                                        <ShieldCheck className="h-4 w-4" /> Two-Factor
                                    </span>
                                    <span className="text-green-600 font-medium">{user?.twoFactorEnabled ? 'Enabled' : 'Disabled'}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-2 text-muted-foreground">
                                        <Clock className="h-4 w-4" /> Last Login
                                    </span>
                                    <span className="font-medium">{user?.lastLogin && formatDateLabel(user?.lastLogin) || 'N/A'}</span>
                                </div>
                                <Separator />
                                <Button variant="destructive" size="sm" className="w-full justify-start text-xs" onClick={handleForceLogout}>
                                    <LogOut className="mr-2 h-3 w-3" /> Force Logout
                                </Button>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Right Column: Activity Feed */}
                    <Card className="md:col-span-2">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle>Recent Activity</CardTitle>
                                <CardDescription>System actions performed by this user</CardDescription>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">

                                {/* Activity Item 1 */}
                                <div className="relative flex items-center justify-between gap-4 pl-10">
                                    <div className="absolute left-0 grid place-content-center w-10 h-10 rounded-full bg-blue-50 border-2 border-white shadow-sm text-blue-600">
                                        <ShieldCheck size={16} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">Updated System Permissions</p>
                                        <p className="text-xs text-muted-foreground">Modified role 'Manager' settings</p>
                                    </div>
                                    <time className="text-xs text-muted-foreground">10 mins ago</time>
                                </div>

                                {/* Activity Item 2 */}
                                <div className="relative flex items-center justify-between gap-4 pl-10">
                                    <div className="absolute left-0 grid place-content-center w-10 h-10 rounded-full bg-slate-50 border-2 border-white shadow-sm text-slate-600">
                                        <Clock size={16} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">User Login</p>
                                        <p className="text-xs text-muted-foreground">Logged in via Chrome on Windows</p>
                                    </div>
                                    <time className="text-xs text-muted-foreground">2 hours ago</time>
                                </div>

                            </div>

                            <Button variant="ghost" className="w-full mt-6 text-sm text-muted-foreground">
                                View All Logs
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </MyContainer>
    );
}